import React from 'react';
import styled from 'styled-components';
import StatsBox from './StatsBox';
import { Flex } from './shared/Flex';
import { isMobile } from '@biosensesignal/web-sdk';

const Wrapper = styled(Flex)`
  display: flex;
  position: absolute;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 80px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 3px;
  padding: 13px 50px;
  bottom: 30px;
  box-sizing: border-box;
`;

const BoxesWrapper = styled(Flex)`
  gap: 30px;
`;

interface VitalSign<T> {
  value: T;
  isEnabled: boolean;
}

interface IStats {
  /**
   *  Object - contains health stats info
   */
  vitalSigns: {
    pulseRate: VitalSign<number>;
    respirationRate: VitalSign<number>;
    stress: VitalSign<number>;
    hrvSdnn: VitalSign<number>;
    spo2: VitalSign<number>;
    bloodPressure: VitalSign<BloodPressureValue>;
  };
}

export type BloodPressureValue = {
  systolic: number;
  diastolic: number;
};

const Stats = ({ vitalSigns }: IStats) => {
  const bloodPressureToDisplay =
    vitalSigns.bloodPressure.value?.systolic &&
    vitalSigns.bloodPressure.value?.diastolic
      ? vitalSigns.bloodPressure.value.systolic +
        '/' +
        vitalSigns.bloodPressure.value.diastolic
      : null;

  // Helper function to check if a metric should be displayed
  const shouldDisplayMetric = (metric: VitalSign<any>) => {
    return metric.isEnabled && 
           metric.value !== null && 
           metric.value !== undefined && 
           metric.value !== '';
  };

  // Build array of metrics to display
  const metricsToShow = [];

  if (shouldDisplayMetric(vitalSigns.pulseRate)) {
    metricsToShow.push(
      <StatsBox
        key="pr"
        title={'PR'}
        value={vitalSigns.pulseRate.value}
      />
    );
  }

  if (shouldDisplayMetric(vitalSigns.respirationRate)) {
    metricsToShow.push(
      <StatsBox
        key="rr"
        title={'RR'}
        value={vitalSigns.respirationRate.value}
      />
    );
  }

  if (shouldDisplayMetric(vitalSigns.stress)) {
    metricsToShow.push(
      <StatsBox
        key="sl"
        title={'SL'}
        value={vitalSigns.stress.value}
      />
    );
  }

  if (shouldDisplayMetric(vitalSigns.hrvSdnn)) {
    metricsToShow.push(
      <StatsBox
        key="sdnn"
        title={'SDNN'}
        value={vitalSigns.hrvSdnn.value}
      />
    );
  }

  if (shouldDisplayMetric(vitalSigns.spo2)) {
    metricsToShow.push(
      <StatsBox
        key="spo2"
        title={'SpO2'}
        value={vitalSigns.spo2.value}
      />
    );
  }

  if (isMobile() && vitalSigns.bloodPressure.isEnabled && bloodPressureToDisplay) {
    metricsToShow.push(
      <StatsBox
        key="bp"
        title={'BP'}
        value={bloodPressureToDisplay}
      />
    );
  }

  // Don't render the component if no metrics are available
  if (metricsToShow.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <BoxesWrapper>
        {metricsToShow}
      </BoxesWrapper>
    </Wrapper>
  );
};

export default Stats;
